import { useCallback, useMemo, useState } from 'react';
import React from 'react';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import DeviceHealthIcons from '../../../Device/components/DeviceHealthIcons/DeviceHealthIcons';
import { ListDecoratedMedisanteDevice, DevicesSortBy } from '../../types';
import { useAppState } from '../../../../common';
import { SortOption, SortState } from '../../../../utils/sort';
import { CheckboxCell, CheckboxHeader, StateCell } from './tableComponents';
import { TableColumn } from '../../../../components/InfiniteTable/types';
import { allGatewayDevices } from '../../../Device/constants';
import { DeviceModels } from '../../../Device/types';
import ActionsMenu from '../../../../components/ActionsMenu/ActionsMenu';
import { MoreHoriz } from '@material-ui/icons';
import { IDialogState } from '../../../../components/ActionsMenu/types';

// eslint-disable-next-line
export function useInfinitePaginationWithRef<T, TRef extends HTMLElement>(
  data: T[]
): [T[], () => void, boolean, number] {
  const pagination = useInfinitePagination(data);
  const result = [...pagination] as [T[], () => void, boolean, number];
  return result;
}

export function useInfinitePaginationWithRoot<T>(
  data: T[]
): [T[], () => void, boolean, number] {
  const pagination = useInfinitePagination(data);
  return pagination;
}

function useInfinitePagination<T>(
  data: T[]
): [T[], () => void, boolean, number] {
  const getPage = useCallback(
    (page: number) => data.slice(0, (page + 1) * size),
    [data]
  );
  const size = 50;
  const [pageNum, setPageNum] = useState(0);
  const showedData = getPage(pageNum);
  const hasMore = showedData.length !== data.length;

  const getNextPage = useCallback(() => {
    var promise = new Promise<{ page: any[]; pageNum: number }>((resolve) => {
      setTimeout(() => {
        const nextPage = pageNum + 1;
        const page = getPage(nextPage);
        resolve({ page, pageNum: nextPage });
      });
    });
    return promise;
  }, [pageNum, getPage]);

  const goNextPage = useCallback(async () => {
    const { pageNum } = await getNextPage();
    setPageNum(pageNum);
  }, [setPageNum, getNextPage]);

  return [showedData, goNextPage, hasMore, pageNum];
}

export const useDevicesTableColumns = (
  selectedDevicesIds: string[],
  sortState: SortState<ListDecoratedMedisanteDevice>,
  updateSortState: React.Dispatch<SortOption<DevicesSortBy>>,
  selectAll: { all: boolean; some: boolean },
  onSelectionChanged: (id: string) => void,
  onSelectAllChanged: () => void,
  actionMenuDialogState: IDialogState,
  actionMenuUpdateDialogState: (action: string) => void,
  totalView: boolean,
  containsBluetooth: boolean
): any[] => {
  const config = useConfig();
  const { updateDeviceQueue } = useAppState();

  const columns = useMemo(() => {
    const tableColumns: TableColumn<ListDecoratedMedisanteDevice>[] = [];
    if (config.isJobsFeatureEnabled) {
      tableColumns.push({
        name: 'checkbox-select',
        header: (
          <CheckboxHeader
            someSelected={selectAll.some}
            allSelected={selectAll.all}
            onSelectAllChanged={onSelectAllChanged}
          />
        ),

        cell: (device: ListDecoratedMedisanteDevice) => (
          <CheckboxCell
            isSelected={selectedDevicesIds.indexOf(device.id) !== -1}
            device={device}
            someSelected={selectAll.some}
            onSelectionChanged={onSelectionChanged}
          />
        ),
        width: 50,
        noPadding: true,
      });
    }

    tableColumns.push({
      header: 'Serial Number',
      name: 'serialNumber',
      width: 250,
      sortState: sortState.serialNumber,
      onHeaderClick: () =>
        updateSortState({ sortBy: DevicesSortBy.SERIAL_NUMBER }),
    });
    tableColumns.push({
      header: 'IMEI',
      name: 'imei',
      width: 185,
      sortState: sortState.imei,
      onHeaderClick: () => updateSortState({ sortBy: DevicesSortBy.IMEI }),
    });

    if (containsBluetooth) {
      tableColumns.push({
        header: 'MAC',
        name: 'mac',
        width: 185,
        sortState: sortState.mac,
        onHeaderClick: () => updateSortState({ sortBy: DevicesSortBy.MAC }),
      });
    }

    tableColumns.push({
      header: 'Model',
      name: 'model',
      width: 105,
      cell: (device: ListDecoratedMedisanteDevice) => (
        <>
          {device.properties && device.properties.model
            ? device.properties.model.join(' ')
            : device.model}
        </>
      ),
      sortState: sortState.model,
      onHeaderClick: () => updateSortState({ sortBy: DevicesSortBy.MODEL }),
    });

    tableColumns.push({
      header: 'Measurements',
      name: 'measurementCount',
      width: 155,
      cell: (device: ListDecoratedMedisanteDevice) => (
        <>
          {allGatewayDevices.includes(device.model as DeviceModels)
            ? '-'
            : (totalView
                ? device.measurementCount || 0
                : device.measurementTripCount || 0
              ).toLocaleString()}
        </>
      ),
      sortState: sortState.measurementCount,
      onHeaderClick: () =>
        updateSortState({ sortBy: DevicesSortBy.MEASUREMENTS }),
    });

    if (config.isDeviceStateFeatureEnabled) {
      tableColumns.push({
        header: 'State',
        name: '',
        cell: (device: ListDecoratedMedisanteDevice) => (
          <StateCell device={device} updateDeviceQueue={updateDeviceQueue} />
        ),
        width: 160,
      });
    }
    tableColumns.push({
      name: 'statuses',
      header: 'Status',
      cell: (d: ListDecoratedMedisanteDevice) => {
        return <DeviceHealthIcons device={d} />;
      },
      sortState: sortState.statuses,
      onHeaderClick: () => updateSortState({ sortBy: DevicesSortBy.STATUS }),
    });
    if (config.isTagsFeatureEnabled) {
      tableColumns.push({
        name: 'Tags',
        header: '',
        cell: (d: ListDecoratedMedisanteDevice) => <>{d.tags.join(', ')}</>,
      });
    }

    tableColumns.push({
      name: '',
      header: '',
      cell: (device: ListDecoratedMedisanteDevice) => (
        <ActionsMenu
          devices={[device]}
          buttonIcon={<MoreHoriz />}
          updateDialogState={actionMenuUpdateDialogState}
          dialogState={actionMenuDialogState}
        />
      ),
      width: 80,
    });

    return tableColumns;
  }, [
    config,
    sortState,
    updateDeviceQueue,
    updateSortState,
    onSelectAllChanged,
    onSelectionChanged,
    selectAll.all,
    selectAll.some,
    selectedDevicesIds,
    actionMenuDialogState,
    actionMenuUpdateDialogState,
    totalView,
    containsBluetooth,
  ]);
  return columns;
};
